/**
 * Dev-only gallery: every @agency/ui primitive in every state, grouped by section, with realistic
 * Agency copy (fictional customers). Renders at /dev/gallery.
 */
import { useMemo, useState, type ReactNode } from 'react';
import {
  Activity,
  Bell,
  BookOpen,
  Bug,
  Copy,
  Database,
  Download,
  Ellipsis,
  ExternalLink,
  FileText,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Mail,
  Notebook,
  Pencil,
  PanelLeft,
  RefreshCw,
  Server,
  Settings,
  Sparkles,
} from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AppShell,
  AsOf,
  AuthScreen,
  Avatar,
  BottomPanel,
  Breadcrumbs,
  Button,
  Callout,
  Card,
  Checkbox,
  Chip,
  Code,
  CommandPalette,
  COMPONENT_STATES,
  CountFooter,
  CoverageRing,
  DataTable,
  DEFAULT_SHELL_LAYOUT,
  Dialog,
  DialogClose,
  Divider,
  DropdownMenu,
  EmptyState,
  Field,
  fmtClock,
  fmtCount,
  fmtDelta,
  fmtDuration,
  fmtMicroUsd,
  fmtRatio,
  fmtRelative,
  fmtTimestamp,
  IconButton,
  Input,
  InsetWell,
  Inspector,
  InspectorSection,
  Kbd,
  KeyValue,
  LeftRail,
  LockScreen,
  Logo,
  MetricCell,
  MetricRow,
  MetricStacked,
  Monogram,
  NotificationCenter,
  PageHeader,
  Pagination,
  Panel,
  Popover,
  ProgressBar,
  SearchInput,
  Select,
  Skeleton,
  SkeletonRows,
  Sparkline,
  SplitPane,
  StatCluster,
  StateBadge,
  StatusDot,
  Switch,
  TabPanel,
  Tabs,
  Textarea,
  Timeline,
  ToastProvider,
  Tooltip,
  TooltipProvider,
  TopBar,
  TopBarContext,
  useToast,
  WorkspaceTabs,
  moveItem,
  type MenuEntry,
  type Notification,
  type RailGroup,
  type ShellLayout,
  type WorkspaceTab,
} from '@agency/ui';

/* ------------------------------------------------------------------------------------------ */
/* fixtures (fictional customers only)                                                          */

const NOW = '2026-09-23T14:08:31.000Z';

interface SourceRow {
  id: string;
  name: string;
  account: string;
  provider: 'Vanta' | 'Drata';
  status: 'PASS' | 'FAIL' | 'NEEDS_ATTENTION' | 'DUE_SOON' | 'UNKNOWN' | 'NOT_APPLICABLE' | 'DEACTIVATED' | 'UNSUPPORTED';
  owner: string;
  failing: number;
  lastChecked: string;
}

const ACCOUNTS = ['Halvorsen Logistics', 'Marrow Biotech', 'Harbor Credit Union', 'Quillon Robotics', 'Bramblewood Health', 'Oriel Freight', 'Saltmarsh Analytics'];
const OWNERS = ['J. Okonkwo', 'P. Lindqvist', 'R. Mehta', 'S. Alvarez', 'T. Nakamura', '--'];
const TEST_NAMES = [
  'MFA enforced for all workforce accounts',
  'Laptop disk encryption enabled',
  'Vulnerability SLA met for critical findings',
  'Access reviews completed quarterly',
  'Vendor security reviews current',
  'Backups tested within 90 days',
  'Endpoint agent installed on monitored computers',
  'Offboarding completed within 24 hours',
  'Security awareness training completed',
  'Production database not publicly accessible',
  'Logging enabled for cloud accounts',
  'Incident response plan reviewed annually',
];
const STATUSES: SourceRow['status'][] = ['PASS', 'PASS', 'PASS', 'FAIL', 'NEEDS_ATTENTION', 'DUE_SOON', 'UNKNOWN', 'NOT_APPLICABLE', 'DEACTIVATED', 'UNSUPPORTED', 'PASS', 'FAIL'];

function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

function makeRows(count: number): SourceRow[] {
  const rnd = seeded(42);
  const base = new Date(NOW).getTime();
  const rows: SourceRow[] = [];
  for (let i = 0; i < count; i += 1) {
    const status = STATUSES[Math.floor(rnd() * STATUSES.length)] ?? 'PASS';
    rows.push({
      id: `tst_${(100000 + i).toString(36).toUpperCase()}`,
      name: TEST_NAMES[Math.floor(rnd() * TEST_NAMES.length)] ?? 'Control test',
      account: ACCOUNTS[Math.floor(rnd() * ACCOUNTS.length)] ?? 'Halvorsen Logistics',
      provider: rnd() > 0.35 ? 'Vanta' : 'Drata',
      status,
      owner: OWNERS[Math.floor(rnd() * OWNERS.length)] ?? '--',
      failing: status === 'FAIL' || status === 'NEEDS_ATTENTION' ? Math.floor(rnd() * 240) : 0,
      lastChecked: new Date(base - Math.floor(rnd() * 30 * 86_400_000)).toISOString(),
    });
  }
  return rows;
}

const STATUS_TONE: Record<SourceRow['status'], 'ok' | 'brand' | 'warn' | 'info' | 'dim'> = {
  PASS: 'ok',
  FAIL: 'brand',
  NEEDS_ATTENTION: 'warn',
  DUE_SOON: 'warn',
  UNKNOWN: 'dim',
  NOT_APPLICABLE: 'dim',
  DEACTIVATED: 'dim',
  UNSUPPORTED: 'dim',
};

const STATUS_LABEL: Record<SourceRow['status'], string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  NEEDS_ATTENTION: 'Needs attention',
  DUE_SOON: 'Due soon',
  UNKNOWN: 'Unknown',
  NOT_APPLICABLE: 'Not applicable',
  DEACTIVATED: 'Deactivated',
  UNSUPPORTED: 'Unsupported',
};

const COLUMNS: ColumnDef<SourceRow, unknown>[] = [
  { accessorKey: 'id', header: 'ID', size: 120, meta: { mono: true, tone: 'mute' } },
  { accessorKey: 'name', header: 'Test', size: 320, meta: { grow: true } },
  { accessorKey: 'account', header: 'Account', size: 180 },
  { accessorKey: 'provider', header: 'Provider', size: 110, meta: { mono: true, tone: 'mute' } },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 180,
    cell: (info) => {
      const s = info.getValue() as SourceRow['status'];
      return <StatusDot tone={STATUS_TONE[s]} label={STATUS_LABEL[s]} tintLabel={STATUS_TONE[s] !== 'dim'} />;
    },
  },
  { accessorKey: 'owner', header: 'Owner', size: 120 },
  { accessorKey: 'failing', header: 'Failing', size: 110, meta: { numeric: true, mono: true }, cell: (info) => fmtCount(info.getValue() as number) },
  { accessorKey: 'lastChecked', header: 'Last checked', size: 150, meta: { mono: true, tone: 'mute' }, cell: (info) => fmtTimestamp(info.getValue() as string) },
];

const RAIL_GROUPS = (active: string): RailGroup[] => [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ id: 'overview', label: 'Overview', icon: LayoutDashboard, active: active === 'overview' }],
  },
  {
    id: 'accounts',
    label: 'Accounts',
    items: [
      {
        id: 'accounts',
        label: 'Accounts',
        icon: Database,
        badge: 10,
        active: active === 'accounts',
        children: [
          { id: 'a1', label: 'Halvorsen Logistics', code: 'HL', pinned: true, active: active === 'accounts', hint: 'Vanta' },
          { id: 'a2', label: 'Marrow Biotech', code: 'MB', pinned: true, hint: 'Drata' },
          { id: 'a3', label: 'Harbor Credit Union', code: 'TC', hint: 'Vanta', accent: 'warn' },
        ],
      },
      { id: 'sources', label: 'Sources', icon: ListChecks, badge: '4,218' },
      { id: 'agendas', label: 'Agendas', icon: FileText },
      { id: 'vulns', label: 'Vulnerabilities', icon: Bug, badge: 12 },
      { id: 'outreach', label: 'Outreach', icon: Mail },
    ],
  },
  {
    id: 'assist',
    label: 'Assist',
    items: [
      { id: 'rumi', label: 'Rumi', icon: Sparkles, shortcut: '⌘5' },
      { id: 'notepad', label: 'Notepad', icon: Notebook },
      { id: 'docs', label: 'Documentation', icon: BookOpen },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'sync', label: 'Sync history', icon: RefreshCw },
      { id: 'templates', label: 'Templates', icon: Pencil, disabled: true },
      { id: 'settings', label: 'Settings', icon: Settings },
    ],
  },
];

const NOTIFICATIONS: Notification[] = [
  { id: 'n1', title: 'Sync complete · Halvorsen Logistics', description: '4,218 tests, 312 documents, 96 people in 2m 05s.', tone: 'ok', at: '2026-09-23T13:52:00Z', scope: 'HALVORSEN', read: false },
  { id: 'n2', title: 'Partial · people failed', description: 'Drata returned 403 on personnel. Other datasets committed.', tone: 'warn', at: '2026-09-23T12:10:00Z', scope: 'MARROW', read: false },
  { id: 'n3', title: 'Entitlement renews in 8m', description: 'Renewal will run in the background. No action needed.', tone: 'info', at: '2026-09-23T09:41:00Z', read: true },
  { id: 'n4', title: 'Outreach send blocked', description: 'Snapshot older than 24h. Sync before sending 14 drafts.', tone: 'brand', at: '2026-09-22T17:30:00Z', scope: 'TIDEWATER', read: true },
];

/* ------------------------------------------------------------------------------------------ */
/* page scaffolding                                                                             */

function Section({ id, title, subtitle, children }: { id: string; title: string; subtitle: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-6" aria-labelledby={`${id}-h`}>
      <div className="mb-4 flex items-end justify-between gap-4 border-b border-hair pb-2">
        <div>
          <h2 id={`${id}-h`} className="text-[15px] font-medium tracking-tight text-ink">
            {title}
          </h2>
          <p className="mt-0.5 font-mono text-2xs uppercase tracking-[0.18em] text-mute">{subtitle}</p>
        </div>
        <a href="#top" className="font-mono text-2xs uppercase tracking-[0.2em] text-mute hover:text-ink">
          Top ↑
        </a>
      </div>
      <div className="grid grid-cols-12 gap-6">{children}</div>
    </section>
  );
}

function Row({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <p className="label-mono mb-2">{label}</p>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

const SECTIONS = [
  ['foundation', 'Foundation'],
  ['brand', 'Brand'],
  ['buttons', 'Buttons'],
  ['status', 'Status'],
  ['identity', 'Identity'],
  ['metrics', 'Metrics'],
  ['content', 'Content'],
  ['forms', 'Forms'],
  ['overlays', 'Navigation and overlays'],
  ['data', 'Data table'],
  ['layout', 'Split panes'],
  ['shell', 'Shell'],
  ['screens', 'Screens'],
] as const;

export function GalleryPage() {
  return (
    <TooltipProvider>
      <ToastProvider>
        <div id="top" className="col-span-12 flex flex-col gap-12">
          <PageHeader
            eyebrow="Agency"
            eyebrowPath="Dev / Gallery"
            title="Component gallery"
            subtitle={`${SECTIONS.length} sections. Every primitive in every state, rendered from @agency/ui.`}
            actions={
              <>
                <Button icon={Download}>Export tokens</Button>
                <Button variant="primary" icon={Sparkles}>
                  Ask Rumi
                </Button>
              </>
            }
          />
          <nav aria-label="Gallery sections" className="-mt-6 flex flex-wrap gap-x-4 gap-y-2">
            {SECTIONS.map(([id, label]) => (
              <a key={id} href={`#${id}`} className="font-mono text-2xs uppercase tracking-[0.2em] text-mute hover:text-ink">
                {label}
              </a>
            ))}
          </nav>
          <FoundationSection />
          <BrandSection />
          <ButtonsSection />
          <StatusSection />
          <IdentitySection />
          <MetricsSection />
          <ContentSection />
          <FormsSection />
          <OverlaysSection />
          <DataSection />
          <LayoutSection />
          <ShellSection />
          <ScreensSection />
          <footer className="flex items-center justify-between border-t border-hair pt-4 font-mono text-2xs uppercase tracking-[0.18em] text-mute">
            <span>Agency · Foundry design system · dev gallery</span>
            <span>{fmtCount(100_000)} rows in the virtualised table</span>
          </footer>
        </div>
      </ToastProvider>
    </TooltipProvider>
  );
}

/* ------------------------------------------------------------------------------------------ */

const TOKENS: Array<[string, string, string]> = [
  ['--bg', '#0A0A0B', 'ground'],
  ['--panel', '#111114', 'panel'],
  ['--card', '#1A1A1F', 'hover / active'],
  ['--line', '#2A2A30', 'outer border'],
  ['--hair', '#1E1E24', 'internal divider'],
  ['--ink', '#F4F4F5', 'primary text'],
  ['--mute', '#8A8A93', 'secondary text (corrected)'],
  ['--dim', '#5F5F66', 'decorative only'],
  ['--brand', '#E5103C', 'accent'],
  ['--brand-hi', '#FF2D55', 'hover / small text'],
  ['--ok', '#3DDC97', 'healthy'],
  ['--warn', '#E5C548', 'warning'],
  ['--info', '#5BC0EB', 'informational'],
];

function FoundationSection() {
  const now = new Date(NOW);
  return (
    <Section id="foundation" title="Foundation" subtitle="tokens · type scale · format helpers">
      <Card title="Color tokens" subtitle="tokens.css" span="col-span-12 lg:col-span-7">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-hair bg-hair sm:grid-cols-3 lg:grid-cols-4">
          {TOKENS.map(([name, hex, role]) => (
            <div key={name} className="flex items-center gap-3 bg-panel px-3 py-2.5">
              <span aria-hidden className="h-6 w-6 shrink-0 rounded-sm border border-line" style={{ background: `var(${name})` }} />
              <span className="min-w-0">
                <span className="block truncate font-mono text-[11px] text-ink">{name}</span>
                <span className="block truncate font-mono text-2xs uppercase tracking-[0.15em] text-mute">
                  {hex} · {role}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Type scale" subtitle="Inter · JetBrains Mono" span="col-span-12 lg:col-span-5">
        <div className="flex flex-col gap-3">
          <p className="text-[28px] font-medium tracking-tight">Page title 28</p>
          <p className="text-[15px] font-medium tracking-tight">Section heading 15</p>
          <p className="text-[13px] font-medium tracking-tight">Card title 13</p>
          <p className="text-sm text-mute">Page subtitle 14 mute</p>
          <p className="text-[13.5px] leading-[1.7] text-ink/90">Body prose 13.5 / 1.7. Overnight the sync committed 4,218 tests for Halvorsen Logistics; two datasets are stale.</p>
          <p className="text-[12.5px]">Table cell 12.5</p>
          <p className="label-mono">Metadata label 10 mono .18em</p>
          <p className="font-mono text-2xs uppercase tracking-[0.25em] text-mute">Breadcrumb 10 mono .25em</p>
          <p className="numeric-hero font-mono text-[72px] leading-none">87</p>
        </div>
      </Card>
      <Card title="Format helpers" subtitle="format.ts" span="col-span-12">
        <KeyValue
          layout="grid"
          columns={4}
          items={[
            { key: 'fmtTimestamp', value: fmtTimestamp(now), mono: true },
            { key: 'fmtClock (ET)', value: fmtClock(now, { timeZone: 'America/New_York' }), mono: true },
            { key: 'fmtRelative', value: `${fmtRelative('2026-09-20T14:08:31Z', now)} · ${fmtRelative('2026-09-27T14:08:31Z', now)}`, mono: true },
            { key: 'fmtRatio', value: fmtRatio(47, 48, 'controls'), mono: true },
            { key: 'fmtDelta', value: `${fmtDelta(12)} · ${fmtDelta(-2, { unit: 'pts', window: '24h' })}`, mono: true },
            { key: 'fmtCount', value: fmtCount(4212882), mono: true },
            { key: 'fmtMicroUsd', value: `${fmtMicroUsd(12_400_000_000)} · ${fmtMicroUsd(4_200)} · ${fmtMicroUsd(4_200_000_000_000, { compact: true })}`, mono: true },
            { key: 'fmtDuration', value: `${fmtDuration(18_000)} · ${fmtDuration(125_000)} · ${fmtDuration(4_320_000)}`, mono: true },
            { key: 'fmtEmpty', value: null, mono: true },
          ]}
        />
      </Card>
    </Section>
  );
}

function BrandSection() {
  return (
    <Section id="brand" title="Brand" subtitle="lockup · mark · tones">
      <Card title="Lockup" subtitle="white on dark · 18px rail size" span="col-span-12 lg:col-span-6">
        <div className="flex flex-col gap-5">
          <Row label="18px (rail)">
            <Logo height={18} />
          </Row>
          <Row label="28px">
            <Logo height={28} />
          </Row>
          <Row label="Black on light">
            <span className="rounded-sm bg-white p-3">
              <Logo height={22} tone="black" />
            </span>
          </Row>
        </div>
      </Card>
      <Card title="Mark" subtitle="white · brand · currentColor" span="col-span-12 lg:col-span-6">
        <div className="flex flex-col gap-5">
          <Row label="Tones">
            <Logo variant="mark" height={24} tone="white" />
            <Logo variant="mark" height={24} tone="brand" />
            <span className="text-ok">
              <Logo variant="mark" height={24} tone="current" title="Online" />
            </span>
            <span className="text-dim">
              <Logo variant="mark" height={24} tone="current" title="Offline" />
            </span>
          </Row>
          <Row label="Sizes">
            <Logo variant="mark" height={14} />
            <Logo variant="mark" height={18} />
            <Logo variant="mark" height={32} />
            <Logo variant="mark" height={48} />
          </Row>
        </div>
      </Card>
    </Section>
  );
}

function ButtonsSection() {
  return (
    <Section id="buttons" title="Buttons" subtitle="primary · tertiary · ghost · icon">
      <Card title="Variants and states" subtitle="text-xs px-3 py-1.5 · 2px radius" span="col-span-12 lg:col-span-8">
        <div className="flex flex-col gap-5">
          <Row label="Primary">
            <Button variant="primary">Sync now</Button>
            <Button variant="primary" icon={Sparkles}>
              Ask Rumi
            </Button>
            <Button variant="primary" loading>
              Connecting
            </Button>
            <Button variant="primary" disabled>
              Send 14 drafts
            </Button>
            <Button variant="primary" size="sm">
              Small
            </Button>
          </Row>
          <Row label="Tertiary">
            <Button>Export</Button>
            <Button icon={Download}>Export JSON</Button>
            <Button loading>Testing connection</Button>
            <Button disabled>Publish</Button>
            <Button size="sm">Small</Button>
          </Row>
          <Row label="Ghost">
            <Button variant="ghost">View all</Button>
            <Button variant="ghost" external>
              Open in Google Docs
            </Button>
            <Button variant="ghost" trailingArrow={false}>
              Manage all
            </Button>
            <Button variant="ghost" disabled>
              Retry failed parts
            </Button>
          </Row>
        </div>
      </Card>
      <Card title="Icon buttons" subtitle="28px · 24px · pressed" span="col-span-12 lg:col-span-4">
        <div className="flex flex-col gap-5">
          <Row label="Tertiary">
            <IconButton icon={RefreshCw} label="Sync now" />
            <IconButton icon={Copy} label="Copy ID" />
            <IconButton icon={ExternalLink} label="Open externally" />
            <IconButton icon={Ellipsis} label="More" disabled />
          </Row>
          <Row label="Ghost and pressed">
            <IconButton icon={PanelLeft} label="Toggle rail" variant="ghost" />
            <IconButton icon={PanelLeft} label="Toggle rail" variant="ghost" pressed />
            <IconButton icon={Bell} label="Notifications" variant="ghost" size="sm" />
          </Row>
        </div>
      </Card>
    </Section>
  );
}

function StatusSection() {
  return (
    <Section id="status" title="Status" subtitle="dots · chips · the ten component states">
      <Card title="Status dots" subtitle="6px · 4px · pulse" span="col-span-12 lg:col-span-4">
        <div className="flex flex-col gap-3">
          <StatusDot tone="ok" label="Connected" />
          <StatusDot tone="warn" label="Token expires in 4d" />
          <StatusDot tone="info" label="In review" />
          <StatusDot tone="brand" label="Disconnected" />
          <StatusDot tone="dim" label="Inactive" />
          <StatusDot tone="brand" label="Critical · pulse" pulse />
          <StatusDot tone="ok" label="4px in dense grids" size={4} />
          <StatusDot tone="ok" label="Sans label, tinted" mono={false} tintLabel />
        </div>
      </Card>
      <Card title="Chips" subtitle="bordered only · CRIT HIGH MED LOW INFO" span="col-span-12 lg:col-span-4">
        <div className="flex flex-col gap-5">
          <Row label="Severity">
            <Chip severity="critical" />
            <Chip severity="high" />
            <Chip severity="medium" />
            <Chip severity="low" />
            <Chip severity="info" />
          </Row>
          <Row label="Tones">
            <Chip tone="ok">Pass</Chip>
            <Chip tone="warn">Due soon</Chip>
            <Chip tone="info">Fixable</Chip>
            <Chip tone="brand">Fail</Chip>
            <Chip tone="mute">N/A</Chip>
          </Row>
          <Row label="Plain">
            <Chip>Vanta</Chip>
            <Chip>Drata</Chip>
            <Chip>Template only</Chip>
          </Row>
        </div>
      </Card>
      <Card title="Component states" subtitle="StateBadge · exact labels" span="col-span-12 lg:col-span-4">
        <div className="flex flex-col gap-2.5">
          {COMPONENT_STATES.map((s) => (
            <StateBadge key={s} state={s} detail={s === 'partial' ? 'people failed' : s === 'rate-limited' ? 'Waiting for Vanta · retry in 18s' : s === 'stale' ? fmtRelative('2026-09-15T08:00:00Z', NOW) : undefined} />
          ))}
        </div>
      </Card>
    </Section>
  );
}

function IdentitySection() {
  return (
    <Section id="identity" title="Identity" subtitle="monograms · avatars">
      <Card title="Monograms" subtitle="square · sm md lg xl · brand warn neutral" span="col-span-12 lg:col-span-7">
        <div className="flex flex-col gap-5">
          <Row label="Sizes">
            <Monogram code="HL" size="sm" label="Halvorsen Logistics" />
            <Monogram code="MB" size="md" label="Marrow Biotech" />
            <Monogram code="TC" size="lg" label="Harbor Credit Union" />
            <Monogram code="QR" size="xl" label="Quillon Robotics" />
          </Row>
          <Row label="Accents">
            <Monogram code="VA" accent="brand" label="Vanta" />
            <Monogram code="DR" accent="warn" label="Drata" />
            <Monogram code="BH" accent="neutral" label="Bramblewood Health" />
          </Row>
        </div>
      </Card>
      <Card title="Avatars" subtitle="brand circle · initials · never photos" span="col-span-12 lg:col-span-5">
        <Row label="Sizes">
          <Avatar name="Amir Tarighat" size={20} />
          <Avatar name="Amir Tarighat" size={24} />
          <Avatar name="Amir Tarighat" size={28} />
          <Avatar name="Priya Lindqvist" size={32} />
          <Avatar name="Rumi" initials="RU" size={40} />
        </Row>
      </Card>
    </Section>
  );
}

function MetricsSection() {
  const posture = [71, 72, 74, 73, 76, 78, 77, 80, 81, 79, 83, 84, 86, 87];
  const mttr = [22, 21, 19, 20, 18, 17, 17, 15, 14, 14, 13, 14, 12, 11];
  const flat = [4, 4, 4, 4, 4, 4, 4, 4];
  return (
    <Section id="metrics" title="Metrics" subtitle="metric cells · sparklines · rings · bars">
      <Card title="Inline 3-up" subtitle="MetricRow + MetricCell" span="col-span-12 lg:col-span-6">
        <MetricRow columns={3}>
          <MetricCell label="Failing tests" value={fmtCount(212)} tone="brand" />
          <MetricCell label="Sync duration" value={fmtDuration(125_000)} />
          <MetricCell label="Coverage" value={fmtRatio(47, 48)} tone="ok" />
        </MetricRow>
        <MetricRow columns={4} className="mt-3">
          <MetricCell label="Tests" value={fmtCount(4218)} />
          <MetricCell label="Documents" value={fmtCount(312)} />
          <MetricCell label="People" value="--" tone="mute" />
          <MetricCell label="Vulnerabilities" value={fmtCount(96)} tone="warn" />
        </MetricRow>
      </Card>
      <Card title="Stacked" subtitle="numeric-hero · text-3xl font-light" span="col-span-12 lg:col-span-6">
        <div className="grid grid-cols-3 gap-6">
          <MetricStacked label="Open asks" value={fmtCount(38)} sub="12 infrastructure · 26 people" icon={ListChecks} />
          <MetricStacked label="Pending drafts" value={fmtCount(14)} tone="warn" sub="snapshot 27h old" icon={Mail} />
          <MetricStacked label="Spend (30d)" value={fmtMicroUsd(184_320_000_000, { compact: true })} tone="ink" sub="est. from token usage" icon={Activity} />
        </div>
      </Card>
      <Card
        title="Sparklines"
        subtitle="1px polyline · valence colouring"
        span="col-span-12 lg:col-span-5"
        rightSlot={
          <span className="flex items-center gap-3 font-mono text-2xs uppercase tracking-[0.18em] text-mute">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-brand" /> bad
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-ok" /> good
            </span>
          </span>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <p className="label-mono mb-1">Hero · always brand</p>
            <Sparkline values={posture} valence="hero" label="Posture score 71 to 87" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="label-mono mb-1">Failing up · bad</p>
              <Sparkline values={posture} size="table" valence="up-bad" />
            </div>
            <div>
              <p className="label-mono mb-1">MTTR down · good</p>
              <Sparkline values={mttr} size="table" valence="up-bad" />
            </div>
            <div>
              <p className="label-mono mb-1">Flat · no signal</p>
              <Sparkline values={flat} size="table" valence="up-bad" />
            </div>
          </div>
        </div>
      </Card>
      <Card title="Coverage rings" subtitle="48px · 4-tier threshold · no % sign" span="col-span-12 lg:col-span-7">
        <div className="flex flex-wrap items-end gap-8">
          {[
            [0.97, 'Halvorsen Logistics'],
            [0.82, 'Marrow Biotech'],
            [0.66, 'Harbor Credit Union'],
            [0.41, 'Quillon Robotics'],
            [null, 'Bramblewood Health'],
          ].map(([v, name]) => (
            <div key={String(name)} className="flex flex-col items-center gap-2">
              <CoverageRing value={v as number | null} label={`${String(name)} coverage`} />
              <span className="max-w-[124px] truncate font-mono text-2xs uppercase tracking-[0.15em] text-mute">{String(name)}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-4">
          {[
            ['SOC 2 · 172/184 controls', 0.93],
            ['ISO 27001 · 84/103 controls', 0.82],
            ['HIPAA · 41/63 controls', 0.65],
            ['PCI DSS 4.0 · 112/220 controls', 0.51],
          ].map(([label, v]) => (
            <div key={String(label)}>
              <div className="mb-1.5 flex justify-between font-mono text-2xs uppercase tracking-[0.15em] text-mute">
                <span>{String(label)}</span>
                <span className="text-ink">{Math.round((v as number) * 100)}</span>
              </div>
              <ProgressBar value={v as number} label={String(label)} />
            </div>
          ))}
          <div>
            <p className="label-mono mb-1.5">Segmented · pass / not applicable / needs attention</p>
            <ProgressBar
              label="Test outcomes"
              segments={[
                { value: 3820, tone: 'ok', label: 'Pass' },
                { value: 186, tone: 'info', label: 'Not applicable' },
                { value: 212, tone: 'warn', label: 'Needs attention' },
              ]}
            />
          </div>
        </div>
      </Card>
    </Section>
  );
}

function ContentSection() {
  return (
    <Section id="content" title="Content" subtitle="cards · wells · timeline · callouts · key-value · empty · skeleton">
      <Card
        title="Card with right slot"
        subtitle="generated by Rumi · 07:00 ET"
        span="col-span-12 lg:col-span-6"
        rightSlot={
          <span className="flex items-center gap-1.5 font-mono text-2xs uppercase tracking-[0.18em] text-brand-text">
            <Sparkles size={11} aria-hidden /> AI analyst
          </span>
        }
        footer={<Button variant="ghost">Open agenda</Button>}
      >
        <p className="text-[13.5px] leading-[1.7] text-ink/90">
          Halvorsen Logistics has 212 failing tests after the overnight sync, 38 more than the previous snapshot. The increase is concentrated in endpoint encryption on 41 newly enrolled laptops. Two policies are due for annual review within 14 days.
        </p>
      </Card>
      <Card title="Pivot strip" subtitle="rightSlot pattern" span="col-span-12 lg:col-span-6" rightSlot={<PivotStrip />} flush>
        <div className="px-5 py-4">
          <InsetWell>
            <p className="label-mono">Inset well</p>
            <p className="mt-1 font-mono text-[13px] text-ink">vanta-halvorsen · secret-halvorsen</p>
          </InsetWell>
          <Panel className="mt-3" raised>
            <p className="text-[13px] text-ink/85">Panel, raised (--card). Used for inset sections inside a card body.</p>
          </Panel>
          <Divider label="or" className="my-4" />
          <div className="flex items-center gap-2 text-[13px] text-ink">
            Press <Kbd>⌘K</Kbd> then <Kbd>↵</Kbd> · Record <Code>tst_2A4F9</Code> · Hash <Code title="sha256">9f2c…b41e</Code>
          </div>
        </div>
      </Card>
      <Card title="Timeline" subtitle="snapshot history" span="col-span-12 lg:col-span-6">
        <Timeline
          items={[
            { id: 't1', meta: ['2026-09-23 13:52Z', 'snap_0931', 'v14'], delta: '+38 failing', deltaTone: 'brand', title: 'Sync complete', summary: '4,218 tests, 312 documents, 96 people. Endpoint dataset grew by 41 laptops.', byline: 'Amir Tarighat · Vanta', nodeTone: 'ok' },
            { id: 't2', meta: ['2026-09-22 08:10Z', 'snap_0930', 'v13'], delta: '-4 failing', title: 'Partial · people failed', summary: 'Drata returned 403 on personnel scope. Previous people dataset retained.', byline: 'Amir Tarighat · Drata', nodeTone: 'warn' },
            { id: 't3', meta: ['2026-09-19 16:44Z', 'agenda', 'rev 7'], title: 'Agenda published to Google Docs', summary: 'Rolling agenda updated; 3 asks closed, 5 carried forward.', byline: 'Amir Tarighat', nodeTone: 'info' },
            { id: 't4', meta: ['2026-09-12 09:02Z', 'snap_0921', 'v12'], title: 'Snapshot expired', summary: 'Retention window 30 days. Latest complete snapshot always kept.', byline: 'system', nodeTone: 'dim' },
          ]}
        />
      </Card>
      <div className="col-span-12 flex flex-col gap-4 lg:col-span-6">
        <Callout tone="info" title="Snapshot is 27h old">
          Outreach eligibility is computed from the latest complete snapshot. Sync before sending to apply the 24h freshness rule.
        </Callout>
        <Callout tone="warn" title="Personnel scope unavailable" actions={<Button size="sm">View connection</Button>}>
          Drata returned 403 for personnel. Employee compliance for Marrow Biotech is marked unsupported, not empty.
        </Callout>
        <Callout tone="critical" title="Entitlement expired" actions={<Button variant="primary" size="sm">Sign in</Button>}>
          Local data stays readable for 41m. Sync, publish and send are disabled until you sign in again.
        </Callout>
        <Callout tone="info">Plain callout without a title. One sentence, no icon fill, 2px left bar.</Callout>
      </div>
      <Card title="Key-value rows" subtitle="connection" span="col-span-12 lg:col-span-4">
        <KeyValue
          items={[
            { key: 'Provider', value: 'Vanta' },
            { key: 'Tenant', value: 'halvorsen-logistics', mono: true },
            { key: 'Client ID', value: 'vanta-halvorsen', mono: true },
            { key: 'Last sync', value: fmtTimestamp('2026-09-23T13:52:00Z'), mono: true },
            { key: 'Status', value: 'Connected', tone: 'ok' },
            { key: 'Workspace', value: null },
          ]}
        />
      </Card>
      <Card title="Empty states" subtitle="one plain sentence" span="col-span-12 lg:col-span-4" flush>
        <EmptyState message="No agendas have been generated for Quillon Robotics." action={<Button size="sm">Generate agenda</Button>} />
        <Divider />
        <EmptyState message="No rows match the current filters." compact />
      </Card>
      <Card title="Loading" subtitle="static skeleton · no shimmer" span="col-span-12 lg:col-span-4" flush>
        <SkeletonRows rows={5} />
        <div className="flex flex-col gap-2 px-5 py-4">
          <Skeleton width="60%" height={14} />
          <Skeleton width="90%" />
          <Skeleton width="40%" />
        </div>
      </Card>
    </Section>
  );
}

function PivotStrip() {
  const [pivot, setPivot] = useState('account');
  return (
    <div className="flex items-center gap-2 font-mono text-2xs uppercase tracking-[0.18em]">
      <span className="text-mute">Pivot:</span>
      {['account', 'framework', 'owner'].map((p) => (
        <button key={p} type="button" aria-pressed={pivot === p} onClick={() => setPivot(p)} className={p === pivot ? 'rounded-sm border border-line px-1.5 py-0.5 text-ink' : 'px-1.5 py-0.5 text-mute transition-colors hover:text-ink'}>
          {p}
        </button>
      ))}
    </div>
  );
}

function FormsSection() {
  const [check, setCheck] = useState<boolean | 'indeterminate'>('indeterminate');
  const [sw, setSw] = useState(true);
  const [provider, setProvider] = useState('vanta');
  return (
    <Section id="forms" title="Forms" subtitle="field · input · textarea · select · checkbox · switch">
      <Card title="Fields" subtitle="mono label · help · error · required" span="col-span-12 lg:col-span-6">
        <div className="flex flex-col gap-5">
          <Field label="Account name" required help="Shown in the rail and on every artifact.">
            <Input defaultValue="Halvorsen Logistics" />
          </Field>
          <Field label="Client ID" hint="Vanta" help="Case is preserved.">
            <Input mono defaultValue="vanta-halvorsen" icon={KeyRound} />
          </Field>
          <Field label="Client secret" error="Vanta rejected these credentials (401). Check the secret and try again.">
            <Input mono type="password" defaultValue="secret-halvorsen" />
          </Field>
          <Field label="Retention (days)" help="Latest complete snapshot is always kept.">
            <Input type="number" defaultValue={30} className="max-w-[120px]" trailing={<Kbd>d</Kbd>} />
          </Field>
          <Field label="Disabled">
            <Input disabled defaultValue="Managed by the server" />
          </Field>
          <Field label="Search" help="Real input, not the rail affordance.">
            <SearchInput value="encryption" onValueChange={() => undefined} count="41 / 4,218" />
          </Field>
        </div>
      </Card>
      <Card title="Controls" subtitle="textarea · select · checkbox · switch" span="col-span-12 lg:col-span-6">
        <div className="flex flex-col gap-5">
          <Field label="Meeting note" hint="240 / 2,000">
            <Textarea defaultValue="Follow up with J. Okonkwo on the 41 unencrypted laptops before the SOC 2 window closes." />
          </Field>
          <Field label="Provider" help="Determines the connection test.">
            <Select
              value={provider}
              onValueChange={setProvider}
              options={[
                { value: 'vanta', label: 'Vanta', hint: 'Manage API' },
                { value: 'drata', label: 'Drata', hint: 'Public API' },
                { value: 'none', label: 'Template only' },
              ]}
            />
          </Field>
          <Field label="Grouped">
            <Select
              placeholder="Choose a workspace"
              options={[
                { label: 'Marrow Biotech', options: [{ value: 'ws-1', label: 'Production' }, { value: 'ws-2', label: 'Clinical' }] },
                { label: 'Disabled', options: [{ value: 'ws-3', label: 'Archived', disabled: true }] },
              ]}
            />
          </Field>
          <Field label="Inline layout" inline help="Label left, control right.">
            <Select disabled placeholder="Disabled" options={[{ value: 'x', label: 'x' }]} />
          </Field>
          <div className="flex flex-col gap-3">
            <Checkbox checked={check} onCheckedChange={setCheck} label="Include OK items in the agenda" description="Indeterminate here; click to cycle." />
            <Checkbox defaultChecked label="Route infrastructure asks to the infrastructure sheet" />
            <Checkbox label="Unchecked" />
            <Checkbox disabled defaultChecked label="Disabled, checked" />
          </div>
          <div className="flex flex-col gap-3">
            <Switch checked={sw} onCheckedChange={setSw} label="Personalised outreach mode" description="Recommended. One draft per recipient." />
            <Switch label="Bcc group mode" />
            <Switch disabled defaultChecked label="Store conversations (server managed)" />
          </div>
        </div>
      </Card>
    </Section>
  );
}

function OverlaysSection() {
  const { notify, notifications, markAllRead, clear } = useToast();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(3);
  const menu: MenuEntry[] = [
    { type: 'label', id: 'l', label: 'Snapshot' },
    { id: 'export', label: 'Export JSON', icon: Download, shortcut: '⌘E', onSelect: () => notify({ title: 'Export started', tone: 'info' }) },
    { id: 'compare', label: 'Compare with previous', icon: Activity, onSelect: () => undefined },
    { type: 'checkbox', id: 'stale', label: 'Show stale datasets', checked: true, onCheckedChange: () => undefined },
    { type: 'submenu', id: 'open', label: 'Open in', icon: ExternalLink, items: [{ id: 'g', label: 'Google Docs', onSelect: () => undefined }, { id: 's', label: 'Google Sheets', onSelect: () => undefined }] },
    { type: 'separator', id: 'sep' },
    { id: 'delete', label: 'Delete snapshot', destructive: true, onSelect: () => undefined },
    { id: 'disabled', label: 'Retry failed parts', disabled: true, onSelect: () => undefined },
  ];
  return (
    <Section id="overlays" title="Navigation and overlays" subtitle="tabs · breadcrumbs · dialog · menu · tooltip · popover · toast · palette">
      <Card title="Tabs" subtitle="inset 2px bar · counts · disabled" span="col-span-12 lg:col-span-6" flush>
        <Tabs
          aria-label="Source explorer"
          items={[
            { value: 'tests', label: 'Tests', count: '4,218' },
            { value: 'docs', label: 'Documents', count: 312 },
            { value: 'people', label: 'People', count: '--' },
            { value: 'vulns', label: 'Vulnerabilities', count: 96 },
            { value: 'vendors', label: 'Vendors', disabled: true },
          ]}
          listClassName="px-1"
        >
          <TabPanel value="tests" className="px-5 py-4 text-[13px] text-ink/85">
            4,218 tests across 7 frameworks. 212 failing, 186 not applicable.
          </TabPanel>
          <TabPanel value="docs" className="px-5 py-4 text-[13px] text-ink/85">
            312 documents. 9 due for review within 30 days.
          </TabPanel>
          <TabPanel value="people" className="px-5 py-4">
            <StateBadge state="unsupported" detail="Drata personnel scope missing" />
          </TabPanel>
          <TabPanel value="vulns" className="px-5 py-4 text-[13px] text-ink/85">
            96 findings grouped into 23 packages. 4 critical.
          </TabPanel>
        </Tabs>
        <div className="border-t border-hair px-5 py-4">
          <Tabs compact aria-label="Compact tabs" items={[{ value: 'a', label: 'Overview' }, { value: 'b', label: 'Raw' }, { value: 'c', label: 'History' }]} />
        </div>
      </Card>
      <Card title="Breadcrumbs and pagination" subtitle="mono · ChevronRight 11" span="col-span-12 lg:col-span-6">
        <div className="flex flex-col gap-5">
          <Breadcrumbs items={[{ label: 'Halvorsen Logistics', href: '#overlays' }, { label: 'Sources', href: '#overlays' }, { label: 'Tests' }]} />
          <Breadcrumbs tracking="0.25em" items={[{ label: 'Admin' }, { label: 'People and access' }, { label: 'Priya Lindqvist' }]} />
          <Divider />
          <div className="flex items-center justify-between gap-4">
            <Pagination page={page} pageCount={12} onPageChange={setPage} />
            <Pagination page={1} pageCount={1} onPageChange={() => undefined} />
          </div>
          <CountFooter total={4218} shown={41} selected={1} noun="tests" right={<StateBadge state="stale" detail="27h" />} className="-mx-5 -mb-4" />
        </div>
      </Card>
      <Card title="Dialog, menu, tooltip, popover" subtitle="hairline panels · no shadow" span="col-span-12 lg:col-span-6">
        <div className="flex flex-wrap items-center gap-3">
          <Dialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            trigger={<Button variant="primary">Send 14 drafts</Button>}
            title="Send 14 personalised drafts"
            subtitle="Harbor Credit Union · outreach"
            description="Confirm sending outreach emails from the connected Google account."
            actions={
              <>
                <DialogClose asChild>
                  <Button>Cancel</Button>
                </DialogClose>
                <Button variant="primary" onClick={() => setDialogOpen(false)}>
                  Send
                </Button>
              </>
            }
          >
            <p>Snapshot is 3h old and within the 24h freshness rule. 14 recipients, 0 duplicates, 2 excluded by policy.</p>
            <Callout tone="warn" className="mt-3">
              Sending is audited and cannot be undone. Drafts remain in Gmail if delivery is not confirmed.
            </Callout>
          </Dialog>
          <DropdownMenu trigger={<Button icon={Ellipsis}>Snapshot actions</Button>} items={menu} />
          <Tooltip content="Sync now" shortcut="⌘S">
            <IconButton icon={RefreshCw} label="Sync now" />
          </Tooltip>
          <Popover trigger={<Button>Context manifest</Button>} title="Context sent to Rumi" width={320}>
            <KeyValue
              items={[
                { key: 'Snapshot', value: 'snap_0931', mono: true },
                { key: 'Datasets', value: 'tests, documents, people' },
                { key: 'Records', value: fmtCount(4626), mono: true },
                { key: 'Store', value: 'false', mono: true, tone: 'ok' },
              ]}
            />
          </Popover>
          <Button onClick={() => setPaletteOpen(true)} icon={Sparkles}>
            Command palette
          </Button>
          <Kbd>⌘K</Kbd>
        </div>
        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          groups={[
            {
              label: 'Accounts',
              items: ACCOUNTS.slice(0, 4).map((a, i) => ({ id: `acct-${i}`, label: a, hint: i % 2 ? 'Drata' : 'Vanta', icon: Database, onSelect: () => notify({ title: `Switched to ${a}`, tone: 'info' }) })),
            },
            {
              label: 'Actions',
              items: [
                { id: 'sync', label: 'Sync now', icon: RefreshCw, shortcut: '⌘S', onSelect: () => notify({ title: 'Sync queued', tone: 'info' }) },
                { id: 'agenda', label: 'Generate agenda preview', icon: FileText, onSelect: () => undefined },
                { id: 'rumi', label: 'Ask Rumi', icon: Sparkles, shortcut: '⌘5', onSelect: () => undefined },
                { id: 'jobs', label: 'Toggle jobs panel', icon: Activity, shortcut: '⌘J', onSelect: () => undefined },
              ],
            },
            { label: 'Navigate', items: [{ id: 'settings', label: 'Settings', icon: Settings, onSelect: () => undefined }, { id: 'docs', label: 'Documentation', icon: BookOpen, disabled: true, onSelect: () => undefined }] },
          ]}
        />
      </Card>
      <Card
        title="Toasts and notification center"
        subtitle="provider · list"
        span="col-span-12 lg:col-span-6"
        rightSlot={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => notify({ title: 'Sync complete · Halvorsen Logistics', description: '4,218 tests in 2m 05s.', tone: 'ok', scope: 'HALVORSEN' })}>
              OK
            </Button>
            <Button size="sm" onClick={() => notify({ title: 'Partial · people failed', description: 'Drata returned 403 on personnel.', tone: 'warn', action: { label: 'Open job', onSelect: () => undefined } })}>
              Warn
            </Button>
            <Button size="sm" onClick={() => notify({ title: 'Outreach send blocked', description: 'Snapshot older than 24h.', tone: 'brand' })}>
              Critical
            </Button>
          </div>
        }
        flush
      >
        <div className="p-4">
          <NotificationCenter items={notifications.length ? notifications : NOTIFICATIONS} now={NOW} onMarkAllRead={markAllRead} onClear={clear} onSelect={() => undefined} height={280} />
        </div>
      </Card>
    </Section>
  );
}

function DataSection() {
  const rows = useMemo(() => makeRows(100_000), []);
  const small = useMemo(() => rows.slice(0, 8), [rows]);
  const [selected, setSelected] = useState<string | null>(rows[2]?.id ?? null);
  const [activated, setActivated] = useState<string | null>(null);
  return (
    <Section id="data" title="Data table" subtitle="TanStack Table v8 · react-virtual · 100,000 rows">
      <Card
        title="Source explorer · tests"
        subtitle={`${fmtCount(rows.length)} rows · sortable · keyboard up/down/enter`}
        span="col-span-12"
        flush
        rightSlot={
          <span className="font-mono text-2xs uppercase tracking-[0.18em] text-mute">
            Selected <span className="normal-case text-ink">{selected ?? '--'}</span> · Activated <span className="normal-case text-ink">{activated ?? '--'}</span>
          </span>
        }
      >
        <DataTable
          aria-label="Tests"
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selected}
          onSelect={(k) => setSelected(k)}
          onActivate={(k) => setActivated(k)}
          defaultSorting={[{ id: 'failing', desc: true }]}
          height={420}
          noun="tests"
          isCritical={(r) => r.status === 'FAIL' && r.failing > 200}
          footerRight={<StateBadge state="complete" />}
        />
      </Card>
      <Card title="Loading" subtitle="state=loading" span="col-span-12 lg:col-span-4" flush>
        <DataTable aria-label="Loading tests" columns={COLUMNS.slice(0, 3)} rows={[]} rowKey={(r) => r.id} state="loading" height={220} noun="tests" />
      </Card>
      <Card title="Empty" subtitle="state=empty" span="col-span-12 lg:col-span-4" flush>
        <DataTable aria-label="Empty tests" columns={COLUMNS.slice(0, 3)} rows={[]} rowKey={(r) => r.id} state="empty" emptyMessage="No tests in this snapshot match the filter." height={220} noun="tests" />
      </Card>
      <Card title="Partial · filtered" subtitle="state=partial · 8 of 100,000 shown" span="col-span-12 lg:col-span-4" flush>
        <DataTable aria-label="Partial tests" columns={COLUMNS.slice(0, 3)} rows={small} total={rows.length} rowKey={(r) => r.id} state="partial" stateDetail="people failed" height={220} noun="tests" />
      </Card>
    </Section>
  );
}

function LayoutSection() {
  const [w, setW] = useState(280);
  const [h, setH] = useState(120);
  return (
    <Section id="layout" title="Split panes" subtitle="pointer drag · arrow keys · min/max · onResize">
      <Card title="Horizontal" subtitle={`primary=first · ${w}px · min 200 max 480`} span="col-span-12 lg:col-span-7" flush>
        <SplitPane direction="horizontal" size={w} onResize={setW} min={200} max={480} label="Resize source list" className="h-[240px]">
          <div className="h-full bg-panel p-4 text-[13px] text-ink/85">Source list. Drag the divider or focus it and press the arrow keys.</div>
          <div className="h-full bg-bg p-4 text-[13px] text-ink/85">Record inspector. Width persists via onResize.</div>
        </SplitPane>
      </Card>
      <Card title="Vertical" subtitle={`primary=second · ${h}px · min 80 max 200`} span="col-span-12 lg:col-span-5" flush>
        <SplitPane direction="vertical" primary="second" size={h} onResize={setH} min={80} max={200} label="Resize jobs panel" className="h-[240px]">
          <div className="h-full bg-bg p-4 text-[13px] text-ink/85">Content area.</div>
          <div className="h-full bg-panel p-4 text-[13px] text-ink/85">Bottom jobs panel.</div>
        </SplitPane>
      </Card>
    </Section>
  );
}

function ShellSection() {
  const [tabs, setTabs] = useState<WorkspaceTab[]>([
    { id: 'overview', label: 'Overview', closeable: false },
    { id: 'sources', label: 'Sources', hint: 'HL' },
    { id: 'agenda', label: 'Agenda · 2026-09-24', dirty: true },
    { id: 'rumi', label: 'Rumi', icon: Sparkles },
  ]);
  const [active, setActive] = useState('sources');
  const [bottomOpen, setBottomOpen] = useState(true);
  const [bottomHeight, setBottomHeight] = useState(160);
  const [bottomTab, setBottomTab] = useState('jobs');
  return (
    <Section id="shell" title="Shell" subtitle="rail · top bar · tabs · page header · bottom panel · inspector">
      <Card title="Left rail · 248px" subtitle="groups · nested accounts · badges · footer" span="col-span-12 lg:col-span-4" flush>
        <div className="h-[620px] overflow-hidden">
          <LeftRail groups={RAIL_GROUPS('accounts')} onSearch={() => undefined} footer={{ name: 'Amir Tarighat', org: 'Agency · Administrator' }} />
        </div>
      </Card>
      <Card title="Collapsed · 56px" subtitle="icon-only · tooltips" span="col-span-12 lg:col-span-2" flush>
        <div className="h-[620px] overflow-hidden">
          <LeftRail collapsed groups={RAIL_GROUPS('overview')} onSearch={() => undefined} footer={{ name: 'Amir Tarighat', org: 'Agency' }} />
        </div>
      </Card>
      <div className="col-span-12 flex flex-col gap-6 lg:col-span-6">
        <Card title="Top bar and tabs" subtitle="h-12 · breadcrumbs · clock · status · workspace tabs (drag or Shift+Arrow to reorder)" flush>
          <TopBar
            breadcrumbs={[{ label: 'Halvorsen Logistics' }, { label: 'Sources' }, { label: 'Tests' }]}
            right={
              <>
                <span className="normal-case tracking-normal text-mute">{fmtClock(NOW, { timeZone: 'America/New_York' })}</span>
                <StatusDot tone="ok" label="Server OK" />
                <TopBarContext>Local mode</TopBarContext>
                <IconButton icon={Bell} label="Notifications" variant="ghost" size="sm" />
              </>
            }
          />
          <WorkspaceTabs
            tabs={tabs}
            activeId={active}
            onSelect={setActive}
            onClose={(id) => setTabs((t) => t.filter((x) => x.id !== id))}
            onAdd={() => setTabs((t) => [...t, { id: `new-${t.length}`, label: `Untitled ${t.length}` }])}
            onReorder={(from, to) => setTabs((t) => moveItem(t, from, to))}
            rightSlot={<AsOf value="2026-09-23 13:52Z" />}
          />
          <div className="px-6 pt-6 pb-2">
            <PageHeader
              eyebrow="Halvorsen Logistics"
              eyebrowPath="Sources / Tests"
              title="Tests"
              subtitle="4,218 tests. 212 failing, 38 more than the previous snapshot."
              actions={
                <>
                  <Button>Export</Button>
                  <Button>Compare</Button>
                  <Button variant="primary" icon={RefreshCw}>
                    Sync now
                  </Button>
                </>
              }
            />
          </div>
          <div className="px-6 pb-6 pt-2">
            <PageHeader
              eyebrow="Admin"
              eyebrowPath="Usage"
              title="Usage"
              subtitle="Estimated from token usage. Reconciled nightly."
              stats={<StatCluster items={[{ label: 'Active keys', value: '4', tone: 'ok' }, { label: 'Devices', value: '10' }, { label: 'Spend (30d)', value: fmtMicroUsd(184_320_000_000, { compact: true }), tone: 'brand' }]} />}
            />
          </div>
        </Card>
        <Card title="Bottom panel" subtitle="collapsible · resizable · tab strip" flush>
          <div className="flex h-[260px] flex-col justify-end bg-bg">
            <BottomPanel
              open={bottomOpen}
              onOpenChange={setBottomOpen}
              height={bottomHeight}
              onResize={setBottomHeight}
              min={100}
              max={220}
              tabs={[
                { id: 'jobs', label: 'Jobs', count: 2, tone: 'ok' },
                { id: 'issues', label: 'Issues', count: 1, tone: 'brand' },
                { id: 'log', label: 'Log' },
              ]}
              activeTab={bottomTab}
              onTabChange={setBottomTab}
              rightSlot={<Button variant="ghost" size="sm" trailingArrow={false}>Clear</Button>}
            >
              <div className="flex flex-col divide-y divide-hair text-[12.5px]">
                <div className="flex items-center justify-between px-4 py-2">
                  <span>Halvorsen Logistics · Vanta · fetching tests 38/43 pages</span>
                  <StateBadge state="loading" detail="Waiting for Vanta · retry in 18s" />
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span>Marrow Biotech · Drata · committing</span>
                  <StateBadge state="partial" detail="people failed" />
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span>Harbor Credit Union · Vanta · succeeded {fmtRelative('2026-09-23T13:52:00Z', NOW)}</span>
                  <StateBadge state="complete" />
                </div>
              </div>
            </BottomPanel>
          </div>
        </Card>
      </div>
      <Card title="Inspector" subtitle="record · sections · collapsible" span="col-span-12 lg:col-span-4" flush>
        <div className="h-[420px]">
          <Inspector title="MFA enforced for all workforce accounts" subtitle="tst_2A4F9 · Vanta · Halvorsen Logistics" onClose={() => undefined} actions={<IconButton icon={Copy} label="Copy ID" variant="ghost" size="sm" />} className="border-l-0">
            <InspectorSection title="Status" rightSlot={<Chip severity="high" />}>
              <StatusDot tone="brand" label="Fail" tintLabel />
              <p className="mt-2">41 of 96 people are missing MFA on Google Workspace. Raw provider status: <Code>NEEDS_ATTENTION</Code>.</p>
            </InspectorSection>
            <InspectorSection title="Effective ask" collapsible>
              <p>Enable MFA for the {'{count}'} listed accounts: {'{children}'}.</p>
              <p className="mt-1 font-mono text-2xs uppercase tracking-[0.15em] text-mute">source · published rule v3</p>
            </InspectorSection>
            <InspectorSection title="Details" collapsible>
              <KeyValue items={[{ key: 'Owner', value: 'J. Okonkwo' }, { key: 'Framework', value: 'SOC 2 · CC6.1' }, { key: 'Last checked', value: fmtTimestamp('2026-09-23T13:40:00Z'), mono: true }]} />
            </InspectorSection>
          </Inspector>
        </div>
      </Card>
      <Card title="Inspector · empty" subtitle="nothing selected" span="col-span-12 lg:col-span-4" flush>
        <div className="h-[420px]">
          <Inspector title="Inspector" className="border-l-0">
            <EmptyState message="Select a row to inspect it." />
          </Inspector>
        </div>
      </Card>
      <Card title="Workspace tabs · variants" subtitle="closeable · dirty · hint · empty" span="col-span-12 lg:col-span-4" flush>
        <WorkspaceTabs tabs={[{ id: 'a', label: 'Overview', closeable: false }]} activeId="a" onSelect={() => undefined} rightSlot={<AsOf value="--" />} />
        <WorkspaceTabs tabs={[]} activeId={null} onSelect={() => undefined} onAdd={() => undefined} />
        <div className="px-5 py-4 text-[13px] text-mute">Empty strip shows only the add button.</div>
      </Card>
    </Section>
  );
}

function ScreensSection() {
  const [layout, setLayout] = useState<ShellLayout>({ ...DEFAULT_SHELL_LAYOUT, inspectorOpen: true, bottomOpen: true, bottomHeight: 140 });
  const [bottomTab, setBottomTab] = useState('jobs');
  const rows = useMemo(() => makeRows(400), []);
  const [selected, setSelected] = useState<string | null>(rows[0]?.id ?? null);
  const selectedRow = rows.find((r) => r.id === selected);
  return (
    <Section id="screens" title="Screens" subtitle="app shell composition · lock screen · sign in">
      <Card title="AppShell" subtitle="rail · top bar · tabs · table · inspector · bottom panel" span="col-span-12" flush>
        <div className="h-[720px] overflow-hidden">
          <AppShell
            className="!h-full !w-full"
            layout={layout}
            onLayoutChange={setLayout}
            rail={<LeftRail width={layout.railWidth} collapsed={layout.railCollapsed} groups={RAIL_GROUPS('sources')} onSearch={() => undefined} footer={{ name: 'Amir Tarighat', org: 'Agency · Operator' }} />}
            topBar={
              <TopBar
                breadcrumbs={[{ label: 'Halvorsen Logistics' }, { label: 'Sources' }, { label: 'Tests' }]}
                leading={<IconButton icon={PanelLeft} label="Toggle rail" variant="ghost" size="sm" pressed={!layout.railCollapsed} onClick={() => setLayout({ ...layout, railCollapsed: !layout.railCollapsed })} />}
                right={
                  <>
                    <span className="normal-case tracking-normal">{fmtClock(NOW, { timeZone: 'America/New_York' })}</span>
                    <StatusDot tone="ok" label="Entitled · 41m" />
                    <TopBarContext>Local mode</TopBarContext>
                  </>
                }
              />
            }
            tabs={<WorkspaceTabs tabs={[{ id: 'o', label: 'Overview', closeable: false }, { id: 's', label: 'Sources' }, { id: 'a', label: 'Agenda · 2026-09-24', dirty: true }]} activeId="s" onSelect={() => undefined} onClose={() => undefined} onAdd={() => undefined} rightSlot={<AsOf value="2026-09-23 13:52Z" />} />}
            content={
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between gap-4 border-b border-hair px-4 py-2">
                  <SearchInput value="" onValueChange={() => undefined} compact placeholder="Filter tests" className="max-w-[280px]" />
                  <div className="flex items-center gap-2">
                    <Chip tone="brand">212 fail</Chip>
                    <Chip tone="warn">96 attention</Chip>
                    <Button size="sm" icon={Download}>
                      Export
                    </Button>
                  </div>
                </div>
                <div className="min-h-0 flex-1">
                  <DataTable aria-label="Tests" columns={COLUMNS} rows={rows} rowKey={(r) => r.id} selectedKey={selected} onSelect={setSelected} height="fill" noun="tests" isCritical={(r) => r.status === 'FAIL' && r.failing > 200} />
                </div>
              </div>
            }
            inspector={
              <Inspector title={selectedRow?.name ?? 'Inspector'} subtitle={selectedRow ? `${selectedRow.id} · ${selectedRow.provider} · ${selectedRow.account}` : undefined} onClose={() => setLayout({ ...layout, inspectorOpen: false })}>
                {selectedRow ? (
                  <>
                    <InspectorSection title="Status">
                      <StatusDot tone={STATUS_TONE[selectedRow.status]} label={STATUS_LABEL[selectedRow.status]} tintLabel={STATUS_TONE[selectedRow.status] !== 'dim'} />
                    </InspectorSection>
                    <InspectorSection title="Details">
                      <KeyValue items={[{ key: 'Owner', value: selectedRow.owner }, { key: 'Failing', value: fmtCount(selectedRow.failing), mono: true }, { key: 'Last checked', value: fmtTimestamp(selectedRow.lastChecked), mono: true }]} />
                    </InspectorSection>
                  </>
                ) : (
                  <EmptyState message="Select a row to inspect it." />
                )}
              </Inspector>
            }
            bottomPanel={
              <BottomPanel
                open={layout.bottomOpen}
                onOpenChange={(o) => setLayout({ ...layout, bottomOpen: o })}
                height={layout.bottomHeight}
                onResize={(h) => setLayout({ ...layout, bottomHeight: h })}
                min={100}
                max={320}
                tabs={[{ id: 'jobs', label: 'Jobs', count: 1, tone: 'ok' }, { id: 'issues', label: 'Issues' }]}
                activeTab={bottomTab}
                onTabChange={setBottomTab}
              >
                <div className="flex items-center justify-between px-4 py-2 text-[12.5px]">
                  <span>Halvorsen Logistics · Vanta · fetching tests 38/43 pages</span>
                  <StateBadge state="loading" detail="Waiting for Vanta · retry in 18s" />
                </div>
              </BottomPanel>
            }
          />
        </div>
      </Card>
      <Card title="Lock screen" subtitle="entitlement expired" span="col-span-12 lg:col-span-6" flush>
        <LockScreen
          className="!h-[520px] !w-full"
          message="Your entitlement could not be renewed because the server was unreachable. Local data stays readable until the window closes."
          remaining="41m"
          onSignIn={() => undefined}
          secondary={{ label: 'Quit', onSelect: () => undefined }}
          footer="amir@getagency.com · device MBP-AT · 1.0.0-local"
        />
      </Card>
      <Card title="Sign in" subtitle="OIDC in the system browser" span="col-span-12 lg:col-span-6" flush>
        <AuthScreen
          className="!min-h-[520px]"
          surface="Workbench"
          message="Sign in with your Agency workforce account. The browser will open; return here when it finishes."
          onSignIn={() => undefined}
          error="Your account is not invited to Agency. Ask an administrator to add you."
          footer="server http://localhost:4400 · local mode · 1.0.0-local"
        >
          <p className="font-mono text-2xs uppercase tracking-[0.15em] text-mute">Dev · persona picker at localhost:4410/idp</p>
        </AuthScreen>
      </Card>
    </Section>
  );
}

export default GalleryPage;
